import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CandidateStatBar } from './CandidateStatBar'
import { RecruitmentSuccess } from './RecruitmentSuccess'
import { RECRUITMENT_SOURCES } from '@/constants/recruitment'
import { usePartiesByNation } from '@/hooks/usePartiesByNation'
import { fetchActiveCandidatesByParty, candidateKeys } from '@/services/candidates'
import { executeRecruitmentInstantly } from '@/services/recruitment'
import { actionPointKeys } from '@/services/actionPoints'
import { partyActionKeys } from '@/services/partyActions'
import type { IdeologyVector, Candidate, Party } from '@/types'
import { Loader2, ArrowLeft } from 'lucide-react'

type PoachStep = 'select_party' | 'select_candidate' | 'confirm' | 'success'

interface PoachingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string
  nationId: string
  partyIdeology: IdeologyVector
  currentTick: number
  onRecruited?: (candidate: Candidate) => void
  onAssignRole?: (candidate: Candidate) => void
}

const POACHED_LOYALTY_PENALTY = 30
const POACHED_MIN_LOYALTY = 20

export function PoachingModal({
  open,
  onOpenChange,
  partyId,
  nationId,
  partyIdeology,
  currentTick,
  onRecruited,
  onAssignRole,
}: PoachingModalProps) {
  const queryClient = useQueryClient()
  const { parties } = usePartiesByNation(nationId)
  const [step, setStep] = useState<PoachStep>('select_party')
  const [selectedParty, setSelectedParty] = useState<Party | null>(null)
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null)
  const [recruitedCandidate, setRecruitedCandidate] = useState<Candidate | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apCost = RECRUITMENT_SOURCES.poached.apCost

  // Fetch candidates for the selected rival party
  const { data: rivalCandidates, isLoading: candidatesLoading } = useQuery({
    queryKey: candidateKeys.activeByParty(selectedParty?.id ?? ''),
    queryFn: () => fetchActiveCandidatesByParty(selectedParty!.id),
    enabled: !!selectedParty,
  })

  // Filter out the player's own party
  const rivalParties = parties?.filter((p) => p.id !== partyId && p.is_active) ?? []

  const handleSelectParty = (party: Party) => {
    setSelectedParty(party)
    setSelectedCandidate(null)
    setStep('select_candidate')
  }

  const handleSelectCandidate = (candidate: Candidate) => {
    setSelectedCandidate(candidate)
    setStep('confirm')
  }

  const handleConfirmPoach = async () => {
    if (!selectedCandidate || !selectedParty) return

    setIsSubmitting(true)
    setError(null)

    // Calculate what the poached candidate will look like
    const adjustedLoyalty = Math.max(
      POACHED_MIN_LOYALTY,
      selectedCandidate.loyalty - POACHED_LOYALTY_PENALTY
    )

    try {
      const result = await executeRecruitmentInstantly({
        partyId,
        nationId,
        tick: currentTick,
        source: 'poached',
        candidateData: {
          name: selectedCandidate.name,
          age: selectedCandidate.age,
          ideology_vector: selectedCandidate.ideology_vector,
          charisma: selectedCandidate.charisma,
          admin_skill: selectedCandidate.admin_skill,
          debate_skill: selectedCandidate.debate_skill,
          scandal_prone: selectedCandidate.scandal_prone,
          ambition: selectedCandidate.ambition,
          loyalty: adjustedLoyalty,
          recognition: selectedCandidate.recognition,
        },
        targetPartyId: selectedParty.id,
        targetCandidateId: selectedCandidate.id,
      })

      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: candidateKeys.activeByParty(partyId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.byParty(partyId) })
      queryClient.invalidateQueries({
        queryKey: candidateKeys.activeByParty(selectedParty.id),
      })
      queryClient.invalidateQueries({
        queryKey: actionPointKeys.byPartyTick(partyId, currentTick),
      })
      queryClient.invalidateQueries({ queryKey: partyActionKeys.byParty(partyId) })

      if (result.success && result.candidate) {
        setRecruitedCandidate(result.candidate)
        setStep('success')
        onRecruited?.(result.candidate)
      } else {
        setError(result.failureReason ?? 'Poaching failed.')
        setStep('select_candidate')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    setError(null)
    if (step === 'select_candidate') {
      setStep('select_party')
      setSelectedParty(null)
    } else if (step === 'confirm') {
      setStep('select_candidate')
      setSelectedCandidate(null)
    }
  }

  const handleAssignRole = () => {
    if (recruitedCandidate) {
      onAssignRole?.(recruitedCandidate)
    }
    onOpenChange(false)
  }

  const handleDone = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        {step === 'select_party' && (
          <>
            <DialogHeader>
              <DialogTitle>Poach from Rival Party</DialogTitle>
              <DialogDescription>
                Select a rival party to poach a candidate from. Cost: {apCost} AP.
              </DialogDescription>
            </DialogHeader>

            {rivalParties.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No rival parties available to poach from.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 py-4 sm:grid-cols-2">
                {rivalParties.map((party) => (
                  <Card
                    key={party.id}
                    className="cursor-pointer transition-all hover:border-primary/50"
                    onClick={() => handleSelectParty(party)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: party.color }}
                        />
                        <CardTitle className="text-sm">{party.name}</CardTitle>
                        <Badge variant="outline" className="ml-auto text-xs">
                          {party.abbreviation}
                        </Badge>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleDone}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'select_candidate' && selectedParty && (
          <>
            <DialogHeader>
              <DialogTitle>Select Target: {selectedParty.name}</DialogTitle>
              <DialogDescription>
                Choose a candidate to recruit from {selectedParty.name}. They will join with reduced
                loyalty.
              </DialogDescription>
            </DialogHeader>

            {error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            {candidatesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !rivalCandidates || rivalCandidates.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                This party has no active candidates to poach.
              </div>
            ) : (
              <div className="max-h-[400px] space-y-2 overflow-y-auto py-4">
                {rivalCandidates.map((candidate) => (
                  <Card
                    key={candidate.id}
                    className="cursor-pointer transition-all hover:border-primary/50"
                    onClick={() => handleSelectCandidate(candidate)}
                  >
                    <CardContent className="flex items-center justify-between p-3">
                      <div className="space-y-1">
                        <div className="font-medium">{candidate.name}</div>
                        <div className="flex gap-3 text-xs text-muted-foreground">
                          <span>Age: {candidate.age}</span>
                          <span>Recognition: {candidate.recognition}%</span>
                          <span>
                            Role: {candidate.role === 'none' ? 'Unassigned' : candidate.role}
                          </span>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        Select
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'confirm' && selectedCandidate && selectedParty && (
          <>
            <DialogHeader>
              <DialogTitle>Confirm Poaching</DialogTitle>
              <DialogDescription>
                Recruit {selectedCandidate.name} from {selectedParty.name}? This costs {apCost} AP
                and the candidate will join with reduced loyalty.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{selectedCandidate.name}</div>
                  <Badge variant="outline">Age: {selectedCandidate.age}</Badge>
                </div>

                <div className="space-y-1.5">
                  <CandidateStatBar
                    label="Loyalty"
                    value={Math.max(
                      POACHED_MIN_LOYALTY,
                      selectedCandidate.loyalty - POACHED_LOYALTY_PENALTY
                    )}
                    colorMode="positive"
                  />
                  <CandidateStatBar
                    label="Recognition"
                    value={selectedCandidate.recognition}
                    colorMode="positive"
                  />
                  <CandidateStatBar
                    label="Charisma"
                    value={selectedCandidate.charisma}
                    colorMode="positive"
                  />
                  <CandidateStatBar
                    label="Admin"
                    value={selectedCandidate.admin_skill}
                    colorMode="positive"
                  />
                  <CandidateStatBar
                    label="Debate"
                    value={selectedCandidate.debate_skill}
                    colorMode="positive"
                  />
                </div>

                <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                  Loyalty reduced by {POACHED_LOYALTY_PENALTY}% due to switching parties. Final
                  loyalty:{' '}
                  {Math.max(
                    POACHED_MIN_LOYALTY,
                    selectedCandidate.loyalty - POACHED_LOYALTY_PENALTY
                  )}
                  %
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleConfirmPoach} disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Recruiting...' : `Poach (${apCost} AP)`}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'success' && recruitedCandidate && (
          <RecruitmentSuccess
            candidate={recruitedCandidate}
            partyIdeology={partyIdeology}
            onAssignRole={handleAssignRole}
            onRecruitAnother={handleDone}
            onDone={handleDone}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
